import React from 'react';

interface FooterProps {
  onDocClick?: (docPath: string) => void;
  /**
   * One-line mode, used while a document is open in the editor
   * (EDITOR_WORKSPACE_PLAN.md §W1). The five-column footer is ~350-400px of
   * marketing chrome an author scrolls past under their own work; the slim
   * line keeps what the author is OWED — the copyright notice and the Terms
   * and Privacy links, which are an obligation and must stay reachable in
   * every state of the page — and hands the rest of the height back.
   */
  slim?: boolean;
}

const Footer: React.FC<FooterProps> = ({ onDocClick, slim = false }) => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { name: 'Feature Comparison', href: '/docs/Feature-Comparison.md' },
      { name: 'Quick Start Guide', href: '/docs/Quick-Start-Guide.md' },
      { name: 'User Guide', href: '/docs/User-Guide.md' },
      { name: 'API Reference', href: '/docs/Generated-Project-API-Reference.md' },
    ],
    resources: [
      { name: 'Documentation Home', href: '/docs/README.md' },
      { name: 'Technical Reference', href: '/docs/Technical-Reference.md' },
      { name: 'Administrator Guide', href: '/docs/Administrator-Guide.md' },
      { name: 'Troubleshooting', href: '/docs/Troubleshooting.md' },
    ],
    company: [
      { name: 'About CAVU Innovations', href: 'https://www.cavuinnovations.com/about' },
      { name: 'Our Solutions', href: 'https://www.cavuinnovations.com/solutions' },
      { name: 'Contact Us', href: 'https://www.cavuinnovations.com/contact' },
      { name: 'Support', href: 'mailto:support@cavuinnovations.com' },
    ],
    legal: [
      { name: 'FAQ', href: '/docs/FAQ.md' },
      { name: 'Glossary', href: '/docs/Glossary.md' },
      { name: 'Privacy Policy', href: 'https://www.cavuinnovations.com/privacy' },
      { name: 'Terms of Service', href: 'https://www.cavuinnovations.com/terms' },
    ],
  };

  const socialLinks = [
    {
      name: 'Twitter',
      icon: (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
        </svg>
      ),
    },
    {
      name: 'GitHub',
      icon: (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
          <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
        </svg>
      ),
    },
    {
      name: 'LinkedIn',
      icon: (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
        </svg>
      ),
    },
  ];

  // THE SLIM FOOTER: one line, nothing an author must scroll past twice. The
  // links come from the same footerLinks table as the full footer rather than
  // being retyped, so the two states cannot drift apart — if the Terms URL
  // moves, it moves in both footers in one edit. The copyright sentence is the
  // full footer's own, unchanged; this mode changes WHEN chrome shows, never
  // what it says.
  if (slim) {
    const obligations = footerLinks.legal.filter(
      (link) => link.name === 'Terms of Service' || link.name === 'Privacy Policy'
    );
    return (
      <footer className="bg-gradient-to-b from-neutral-50 to-neutral-100 border-t border-neutral-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-sm text-neutral-600">
            © {currentYear} PDF2Production by CAVU Innovations. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            {obligations.map((link) => (
              <a
                key={link.name}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-neutral-600 hover:text-primary-600 transition-colors duration-200"
              >
                {link.name}
              </a>
            ))}
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-gradient-to-b from-neutral-50 to-neutral-100 border-t border-neutral-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 lg:py-16">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4 lg:grid-cols-5">
            {/* Brand Section */}
            <div className="col-span-2 md:col-span-4 lg:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <svg
                  className="h-10 w-10 text-primary-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                  />
                </svg>
                <div>
                  <span className="text-xl font-display font-bold text-neutral-900">
                    PDF2Production
                  </span>
                  <div className="text-sm text-neutral-600 mt-1">
                    by <a href="https://www.cavuinnovations.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">CAVU Innovations</a>
                  </div>
                </div>
              </div>
              <p className="text-sm text-neutral-600 mb-4">
                Transform your PDF forms into production-ready projects with automated deployment to Docker and Kubernetes. 
                <a href="https://www.cavuinnovations.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
                  Learn more about our innovation solutions
                </a>
              </p>
              <div className="flex space-x-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href="#"
                    className="text-neutral-400 hover:text-primary-600 transition-colors duration-200"
                  >
                    <span className="sr-only">{social.name}</span>
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Links Sections */}
            <div>
              <h3 className="text-sm font-semibold text-neutral-900 mb-3">Product</h3>
              <ul className="space-y-2">
                {footerLinks.product.map((link) => (
                  <li key={link.name}>
                    <button
                      onClick={() => onDocClick?.(link.href)}
                      className="text-sm text-neutral-600 hover:text-primary-600 transition-colors duration-200"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-neutral-900 mb-3">Resources</h3>
              <ul className="space-y-2">
                {footerLinks.resources.map((link) => (
                  <li key={link.name}>
                    <button
                      onClick={() => onDocClick?.(link.href)}
                      className="text-sm text-neutral-600 hover:text-primary-600 transition-colors duration-200"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-neutral-900 mb-3">Company</h3>
              <ul className="space-y-2">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="text-sm text-neutral-600 hover:text-primary-600 transition-colors duration-200"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-neutral-900 mb-3">Help & Legal</h3>
              <ul className="space-y-2">
                {footerLinks.legal.map((link) => (
                  <li key={link.name}>
                    {link.href.startsWith('/docs/') ? (
                      <button
                        onClick={() => onDocClick?.(link.href)}
                        className="text-sm text-neutral-600 hover:text-primary-600 transition-colors duration-200"
                      >
                        {link.name}
                      </button>
                    ) : (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-neutral-600 hover:text-primary-600 transition-colors duration-200"
                      >
                        {link.name}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-neutral-200 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
            <div className="text-sm text-neutral-600">
              <p>© {currentYear} PDF2Production by CAVU Innovations. All rights reserved.</p>
              <p className="mt-1">
                Visit <a href="https://www.cavuinnovations.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">www.cavuinnovations.com</a> for more innovative solutions
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="flex items-center text-sm text-neutral-600">
                <svg
                  className="h-4 w-4 text-success-500 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                SSL Secured
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;